/**
 * @fololabs/design — Mantine v7 theme (brand contract for all products).
 *
 * Assembles the brand tokens (`./tokens`) into a Mantine theme. Every
 * FoloLabs product wraps its tree in
 *   <MantineProvider theme={fololabsTheme}>
 * so Card/Badge/Table/Button/Form etc. render brand-consistent without
 * per-app styling. Component overrides here are minimal brand-tightening
 * only — never product-specific.
 *
 * Stability: theme shape is part of the public contract. Additive theme
 * changes = MINOR; changing the rendered look of an existing primitive or
 * removing a token = MAJOR. See README.
 */

import {
  createTheme,
  rem,
  Button,
  Card,
  Badge,
  Table,
  Skeleton,
  TextInput,
  Textarea,
  Select,
  Modal,
  Anchor,
  Tooltip,
  Notification,
  Paper,
  Tabs,
} from '@mantine/core';

import { colors, sansStack, monoStack, otherTokens } from './tokens';

export const fololabsTheme = createTheme({
  primaryColor: 'aquifer',
  // Aquifer index 7 = #0E2A47 in light mode; index 5 lifts a touch in dark mode.
  primaryShade: { light: 7, dark: 5 },

  colors,

  white: '#FFFFFF',
  black: '#0A1320', // Slate, not pure black — brand canon.

  fontFamily: sansStack,
  fontFamilyMonospace: monoStack,

  fontSizes: {
    xs: rem(11),
    sm: rem(13),
    md: rem(14),
    lg: rem(16),
    xl: rem(18),
  },

  lineHeights: {
    xs: '1.4',
    sm: '1.45',
    md: '1.55',
    lg: '1.6',
    xl: '1.6',
  },

  headings: {
    fontFamily: sansStack,
    fontWeight: '600',
    sizes: {
      h1: { fontSize: rem(36), lineHeight: '1.2', fontWeight: '600' },
      h2: { fontSize: rem(28), lineHeight: '1.25', fontWeight: '600' },
      h3: { fontSize: rem(22), lineHeight: '1.3', fontWeight: '600' },
      h4: { fontSize: rem(18), lineHeight: '1.35', fontWeight: '500' },
      h5: { fontSize: rem(15), lineHeight: '1.4', fontWeight: '500' },
      h6: { fontSize: rem(13), lineHeight: '1.4', fontWeight: '500' },
    },
    textWrap: 'balance',
  },

  spacing: {
    xs: rem(6),
    sm: rem(10),
    md: rem(16),
    lg: rem(24),
    xl: rem(36),
  },

  radius: {
    xs: rem(2),
    sm: rem(4),
    md: rem(6),
    lg: rem(10),
    xl: rem(16),
  },
  defaultRadius: 'md',

  // Subtle, water-toned shadows. Never plain black drops.
  shadows: {
    xs: '0 1px 2px rgba(14, 42, 71, 0.04)',
    sm: '0 1px 3px rgba(14, 42, 71, 0.06), 0 1px 2px rgba(14, 42, 71, 0.04)',
    md: '0 4px 8px -2px rgba(14, 42, 71, 0.08), 0 2px 4px -1px rgba(14, 42, 71, 0.05)',
    lg: '0 10px 24px -6px rgba(14, 42, 71, 0.12), 0 4px 10px -3px rgba(14, 42, 71, 0.06)',
    xl: '0 24px 48px -12px rgba(14, 42, 71, 0.18)',
    // Premium mockup tokens.
    card: '0 1px 2px rgba(14,42,71,0.04), 0 4px 12px rgba(14,42,71,0.06)',
    elev: '0 4px 16px rgba(14,42,71,0.08), 0 16px 48px rgba(14,42,71,0.10)',
    hover: '0 8px 24px rgba(14,42,71,0.12)',
    glass: '0 4px 24px rgba(14,42,71,0.08), inset 0 1px 0 rgba(255,255,255,0.4)',
  },

  focusRing: 'auto',
  cursorType: 'pointer',

  // ── Component overrides — minimal brand-tightening only ──
  components: {
    Button: Button.extend({
      defaultProps: { radius: 'md', size: 'sm' },
      styles: {
        root: { fontWeight: 500, letterSpacing: '-0.005em' },
      },
    }),

    Card: Card.extend({
      defaultProps: { radius: 'lg', padding: 'lg', withBorder: true, shadow: 'xs' },
      styles: {
        root: { borderColor: '#E5E9EE', backgroundColor: '#FFFFFF' },
      },
    }),

    Paper: Paper.extend({
      defaultProps: { radius: 'lg', withBorder: false },
    }),

    Badge: Badge.extend({
      defaultProps: { radius: 'sm', variant: 'light', size: 'sm' },
      styles: {
        root: { fontWeight: 500, textTransform: 'none', letterSpacing: '0' },
      },
    }),

    Table: Table.extend({
      defaultProps: {
        striped: false,
        highlightOnHover: true,
        withTableBorder: true,
        withColumnBorders: false,
        verticalSpacing: 'sm',
        horizontalSpacing: 'md',
      },
      styles: {
        th: {
          fontWeight: 500,
          fontSize: rem(12),
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: '#5E6B7C',
          backgroundColor: '#FBFCFD',
        },
        td: { fontSize: rem(13) },
      },
    }),

    Skeleton: Skeleton.extend({
      defaultProps: { radius: 'sm' },
    }),

    TextInput: TextInput.extend({
      defaultProps: { radius: 'md', size: 'sm' },
    }),

    Textarea: Textarea.extend({
      defaultProps: { radius: 'md', size: 'sm', autosize: true, minRows: 3 },
    }),

    Select: Select.extend({
      defaultProps: { radius: 'md', size: 'sm', checkIconPosition: 'right' },
    }),

    Modal: Modal.extend({
      defaultProps: {
        radius: 'lg',
        padding: 'lg',
        centered: true,
        overlayProps: { backgroundOpacity: 0.55, blur: 3 },
      },
    }),

    Anchor: Anchor.extend({
      defaultProps: { c: 'spring.6', underline: 'hover' },
    }),

    Tooltip: Tooltip.extend({
      defaultProps: { radius: 'sm', color: 'slate.8', withArrow: true, openDelay: 200 },
    }),

    Notification: Notification.extend({
      defaultProps: { radius: 'md', withBorder: true },
    }),

    Tabs: Tabs.extend({
      defaultProps: { radius: 'md', variant: 'default' },
    }),
  },

  other: otherTokens,
});

export default fololabsTheme;
